import React, { ChangeEvent, FunctionComponent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { SlideIn, SubmitButton } from 'components';
import { MotorFullPageProps } from 'scenes/motor/types';
import {
  getManualVehicleDescription,
  getIsRegoKnown,
  getMissingLicencePlate,
} from 'scenes/motor/selectors';
import { YesNoAnswer, YES_NO_OPTIONS } from 'scenes/common/types';
import {
  InputQuestion,
  SegmentedControlQuestion,
} from 'scenes/common/components/Questions';

const IS_REGO_KNOWN = 'isRegoKnown';
const MISSING_REGO = 'missingLicencePlate';

const MIN_REGO_LENGTH = 2;
const MAX_REGO_LENGTH = 10;
const ALPHANUMERIC_PATTERN = /^[A-Za-z0-9]*$/;

const MissingRegoStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onNext,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    trigger,
    watch,
    getValues,
  } = useForm({
    mode: 'onChange',
  });
  const regoKnown = getIsRegoKnown(funnelState);

  const watchedIsRegoKnown = watch(IS_REGO_KNOWN, regoKnown);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (value.match(ALPHANUMERIC_PATTERN)) {
        onChange(value);
      }
    },
    []
  );

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <SegmentedControlQuestion
        errors={errors}
        control={control}
        stateKey={IS_REGO_KNOWN}
        options={YES_NO_OPTIONS}
        defaultValue={regoKnown}
        changeHandler={(
          evt: ChangeEvent<HTMLInputElement>,
          onChange: Function
        ) => {
          onChange(evt);
          trigger();
        }}
        question="Do you know the registration number of the car that you would like to insure?"
        helpText={getManualVehicleDescription(funnelState)}
      />
      {watchedIsRegoKnown === YesNoAnswer.Yes && (
        <SlideIn>
          <InputQuestion
            errors={errors}
            control={control}
            stateKey={MISSING_REGO}
            ariaRequired
            defaultValue={getMissingLicencePlate(funnelState)}
            changeHandler={handleChange}
            maxLength={MAX_REGO_LENGTH}
            validationRules={{
              validate: {
                validRego: (value) =>
                  getValues(IS_REGO_KNOWN) === YesNoAnswer.No ||
                  (value.match(ALPHANUMERIC_PATTERN) &&
                    value.length >= MIN_REGO_LENGTH &&
                    value.length <= MAX_REGO_LENGTH),
              },
            }}
            placeholder="e.g. RF52000"
            question="What is the registration number?"
            helpText="The car registration must be between 2 and 10 alpha numeric characters."
          />
        </SlideIn>
      )}

      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default MissingRegoStep;
